import { useEffect, useState } from 'react';
import {
  Button,
  Divider,
  Group,
  Modal,
  MultiSelect,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { DatePickerInput, TimeInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabaseClient';
import { fetchPublicProfiles } from '../../lib/publicProfiles';
import { profileDisplayName } from '../../lib/forms';
import type { ScheduleCategory } from './AblaufplanCategoryManager';

export type ScheduleEntry = {
  id: string;
  form_id: string;
  category_id: string | null;
  entry_date: string;
  start_time: string;
  end_time: string;
  title: string;
  description: string | null;
  location: string | null;
  sort_order: number;
  version: number;
  is_app_visible: boolean;
  visible_until: string | null;
};

type AssignmentType = 'all' | 'individual' | 'rule' | 'role' | 'user';

type Props = {
  opened: boolean;
  onClose: () => void;
  formId: string;
  entry?: ScheduleEntry | null;
  categories: ScheduleCategory[];
};

function dateToLocal(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function stripSeconds(t: string): string {
  return t.slice(0, 5);
}

export function AblaufplanEntryModal({ opened, onClose, formId, entry, categories }: Props) {
  const qc = useQueryClient();
  const isEdit = !!entry;

  // ── Core fields ─────────────────────────────────────────────────────────
  const [dateStr, setDateStr] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:00');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [isAppVisible, setIsAppVisible] = useState(true);
  const [visibleUntilStr, setVisibleUntilStr] = useState('');

  // ── Assignment fields ────────────────────────────────────────────────────
  const [includeAllParticipants, setIncludeAllParticipants] = useState(true);
  const [selectedSubmissionIds, setSelectedSubmissionIds] = useState<string[]>([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [ruleEnabled, setRuleEnabled] = useState(false);
  const [ruleFieldKey, setRuleFieldKey] = useState('');
  const [ruleOperator, setRuleOperator] = useState<string>('contains');
  const [ruleValue, setRuleValue] = useState('');

  // ── Queries for assignment UI ────────────────────────────────────────────
  // Load submissions + profiles for proper display names
  const submissionsQ = useQuery({
    queryKey: ['form-submissions-with-names', formId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('form_submissions')
        .select('id, user_id')
        .eq('form_id', formId);
      if (error) throw error;
      const rows = (data ?? []) as Array<{ id: string; user_id: string | null }>;

      // Fetch profiles for all user_ids to get real names
      const userIds = rows.map((r) => r.user_id).filter(Boolean) as string[];
      const profiles = userIds.length > 0 ? await fetchPublicProfiles(userIds) : [];
      const profileById = new Map(profiles.map((p) => [p.id, p]));

      return rows.map((row, idx) => {
        const profile = row.user_id ? profileById.get(row.user_id) : undefined;
        let label: string;
        if (profile) {
          const name = profileDisplayName(profile as Record<string, unknown>);
          label = profile.nickname && name !== profile.nickname
            ? `${name} (${profile.nickname})`
            : name || `Teilnehmer ${idx + 1}`;
        } else {
          label = `Teilnehmer ${idx + 1}`;
        }
        return { id: row.id, user_id: row.user_id, label };
      });
    },
    enabled: opened,
  });

  const rolesQ = useQuery({
    queryKey: ['all-roles'],
    queryFn: async () => {
      const { data, error } = await supabase.from('roles').select('id, name').order('name');
      if (error) throw error;
      return (data ?? []) as Array<{ id: string; name: string }>;
    },
    enabled: opened,
  });

  const allProfilesQ = useQuery({
    queryKey: ['all-profiles-for-assignment'],
    queryFn: async () => {
      const profiles = await fetchPublicProfiles();
      return profiles;
    },
    enabled: opened,
  });

  const fieldsQ = useQuery({
    queryKey: ['form-fields-simple', formId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('form_fields')
        .select('field_key, label, field_type')
        .eq('form_id', formId)
        .in('field_type', ['text_input', 'select', 'radio', 'multiselect', 'checkbox_group', 'textarea', 'email', 'text']);
      if (error) throw error;
      return (data ?? []) as Array<{ field_key: string; label: string; field_type: string }>;
    },
    enabled: opened,
  });

  const existingAssignmentsQ = useQuery({
    queryKey: ['entry-assignments', entry?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('schedule_entry_assignments')
        .select('*')
        .eq('entry_id', entry!.id);
      if (error) throw error;
      return data as Array<{
        id: string; entry_id: string; assignment_type: AssignmentType;
        submission_id: string | null; rule_field_key: string | null;
        rule_operator: string | null; rule_value: string | null;
        role_id: string | null; assigned_user_id: string | null;
      }>;
    },
    enabled: opened && !!entry?.id,
  });

  // ── Initialise form when opened ──────────────────────────────────────────
  useEffect(() => {
    if (!opened) return;
    if (entry) {
      setDateStr(entry.entry_date);
      setStartTime(stripSeconds(entry.start_time));
      setEndTime(stripSeconds(entry.end_time));
      setTitle(entry.title);
      setDescription(entry.description ?? '');
      setLocation(entry.location ?? '');
      setCategoryId(entry.category_id);
      setIsAppVisible(entry.is_app_visible);
      setVisibleUntilStr(entry.visible_until ?? '');
      setIncludeAllParticipants(true);
      setSelectedSubmissionIds([]);
      setSelectedRoleIds([]);
      setSelectedUserIds([]);
      setRuleEnabled(false);
      setRuleFieldKey('');
      setRuleOperator('contains');
      setRuleValue('');
    } else {
      const today = new Date();
      setDateStr(
        `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
      );
      setStartTime('09:00');
      setEndTime('10:00');
      setTitle('');
      setDescription('');
      setLocation('');
      setCategoryId(null);
      setIsAppVisible(true);
      setVisibleUntilStr('');
      setIncludeAllParticipants(true);
      setSelectedSubmissionIds([]);
      setSelectedRoleIds([]);
      setSelectedUserIds([]);
      setRuleEnabled(false);
      setRuleFieldKey('');
      setRuleOperator('contains');
      setRuleValue('');
    }
  }, [opened, entry]);

  // Load existing assignments when editing
  useEffect(() => {
    if (!opened || !existingAssignmentsQ.data) return;
    const assignments = existingAssignmentsQ.data;
    if (assignments.length === 0) {
      setIncludeAllParticipants(true);
      setSelectedSubmissionIds([]);
      setSelectedRoleIds([]);
      setSelectedUserIds([]);
      setRuleEnabled(false);
      setRuleFieldKey('');
      setRuleOperator('contains');
      setRuleValue('');
      return;
    }
    setIncludeAllParticipants(assignments.some((a) => a.assignment_type === 'all'));
    setSelectedSubmissionIds(
      assignments
        .filter((a) => a.assignment_type === 'individual' && a.submission_id)
        .map((a) => a.submission_id as string)
    );
    setSelectedRoleIds(
      assignments
        .filter((a) => a.assignment_type === 'role' && a.role_id)
        .map((a) => a.role_id as string)
    );
    setSelectedUserIds(
      assignments
        .filter((a) => a.assignment_type === 'user' && a.assigned_user_id)
        .map((a) => a.assigned_user_id as string)
    );
    const firstRule = assignments.find((a) => a.assignment_type === 'rule');
    setRuleEnabled(Boolean(firstRule));
    setRuleFieldKey(firstRule?.rule_field_key ?? '');
    setRuleOperator(firstRule?.rule_operator ?? 'contains');
    setRuleValue(firstRule?.rule_value ?? '');
  }, [opened, existingAssignmentsQ.data]);

  // ── Validation ────────────────────────────────────────────────────────────
  const validate = (): string | null => {
    if (!dateStr) return 'Datum ist erforderlich';
    if (!title.trim()) return 'Titel ist erforderlich';
    if (startTime >= endTime) return 'Endzeit muss nach der Startzeit liegen';
    if (
      !includeAllParticipants
      && selectedSubmissionIds.length === 0
      && selectedRoleIds.length === 0
      && selectedUserIds.length === 0
      && !ruleEnabled
    ) {
      return 'Bitte mindestens eine Teilnehmerzuordnung auswählen.';
    }
    if (!includeAllParticipants && ruleEnabled && (!ruleFieldKey || !ruleValue.trim()))
      return 'Bitte Feld und Wert für die Regel angeben.';
    return null;
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const saveMutation = useMutation({
    mutationFn: async () => {
      const validationError = validate();
      if (validationError) throw new Error(validationError);
      const visibleUntilParam = visibleUntilStr || null;

      let entryId: string;

      if (isEdit && entry) {
        const result = await supabase.rpc('update_schedule_entry', {
          p_id: entry.id,
          p_expected_version: entry.version,
          p_title: title.trim(),
          p_description: description.trim() || null,
          p_location: location.trim() || null,
          p_entry_date: dateStr,
          p_start_time: startTime,
          p_end_time: endTime,
          p_category_id: categoryId,
          p_sort_order: entry.sort_order,
          p_is_app_visible: isAppVisible,
          p_visible_until: visibleUntilParam,
        });
        const data = result.data as { ok: boolean; error?: string };
        if (!data.ok) {
          throw new Error(
            data.error === 'conflict'
              ? 'Dieser Eintrag wurde zwischenzeitlich geändert. Bitte neu laden.'
              : (data.error ?? 'Unbekannter Fehler')
          );
        }
        entryId = entry.id;
        // Delete old assignments before re-inserting
        await supabase.from('schedule_entry_assignments').delete().eq('entry_id', entryId);
      } else {
        const { data: newRow, error } = await supabase
          .from('schedule_entries')
          .insert({
            form_id: formId,
            category_id: categoryId,
            entry_date: dateStr,
            start_time: startTime,
            end_time: endTime,
            title: title.trim(),
            description: description.trim() || null,
            location: location.trim() || null,
            is_app_visible: isAppVisible,
            visible_until: visibleUntilParam,
          })
          .select('id')
          .single();
        if (error) throw error;
        entryId = newRow.id;
      }

      // Insert assignments
      const assignmentRows: Array<Record<string, string | null>> = [];
      if (includeAllParticipants) {
        assignmentRows.push({ entry_id: entryId, assignment_type: 'all' });
      } else {
        assignmentRows.push(
          ...selectedSubmissionIds.map((sid) => ({
            entry_id: entryId,
            assignment_type: 'individual',
            submission_id: sid,
          }))
        );
        assignmentRows.push(
          ...selectedRoleIds.map((rid) => ({
            entry_id: entryId,
            assignment_type: 'role',
            role_id: rid,
          }))
        );
        assignmentRows.push(
          ...selectedUserIds.map((uid) => ({
            entry_id: entryId,
            assignment_type: 'user',
            assigned_user_id: uid,
          }))
        );
        if (ruleEnabled) {
          assignmentRows.push({
            entry_id: entryId,
            assignment_type: 'rule',
            rule_field_key: ruleFieldKey,
            rule_operator: ruleOperator,
            rule_value: ruleValue.trim(),
          });
        }
      }
      if (assignmentRows.length > 0) {
        await supabase.from('schedule_entry_assignments').insert(assignmentRows);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedule-entries', formId] });
      qc.invalidateQueries({ queryKey: ['entry-assignments', entry?.id] });
      qc.invalidateQueries({ queryKey: ['schedule-all-assignments', formId] });
      onClose();
    },
    onError: (err: Error) =>
      notifications.show({ color: 'red', title: 'Fehler', message: err.message }),
  });

  // ── Select options ────────────────────────────────────────────────────────
  const categoryOptions = categories.map((c) => ({
    value: c.id,
    label: `${c.icon ? c.icon + ' ' : ''}${c.name}`,
  }));

  const submissionOptions = (submissionsQ.data ?? []).map((s) => ({
    value: s.id,
    label: s.label,
  }));

  const fieldOptions = (fieldsQ.data ?? []).map((f) => ({
    value: f.field_key,
    label: f.label || f.field_key,
  }));

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEdit ? 'Eintrag bearbeiten' : 'Neuer Eintrag'}
      size="md"
    >
      <Stack>
        {/* Core fields */}
        <DatePickerInput
          label="Datum"
          placeholder="Datum wählen"
          value={dateStr ? dateToLocal(dateStr) : null}
          onChange={(val: unknown) => {
            if (val instanceof Date) {
              setDateStr(`${val.getFullYear()}-${String(val.getMonth() + 1).padStart(2, '0')}-${String(val.getDate()).padStart(2, '0')}`);
            }
          }}
          required
          locale="de"
        />
        <Group grow>
          <TimeInput
            label="Startzeit"
            value={startTime}
            onChange={(e) => setStartTime(e.currentTarget.value)}
            required
          />
          <TimeInput
            label="Endzeit"
            value={endTime}
            onChange={(e) => setEndTime(e.currentTarget.value)}
            required
          />
        </Group>
        {startTime >= endTime && startTime && endTime && (
          <Text size="xs" c="red">Endzeit muss nach der Startzeit liegen</Text>
        )}
        <TextInput
          label="Titel"
          placeholder="z.B. Chorprobe"
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
          required
        />
        <TextInput
          label="Ort"
          placeholder="z.B. Proberaum 1"
          value={location}
          onChange={(e) => setLocation(e.currentTarget.value)}
        />
        <Textarea
          label="Beschreibung"
          placeholder="Optionale Details…"
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          rows={2}
        />
        <Select
          label="Kategorie"
          placeholder="Keine Kategorie"
          value={categoryId}
          onChange={setCategoryId}
          data={categoryOptions}
          clearable
        />

        <Divider label="Teilnehmerzuordnung" labelPosition="center" />

        <Text size="sm" c="dimmed">
          Rollen, einzelne Nutzer und einzelne Einreichungen lassen sich hier kombinieren.
        </Text>

        <Switch
          label="👥 Alle Teilnehmer"
          description="Wenn aktiv, gilt der Eintrag für alle und die gezielten Zuordnungen darunter werden beim Speichern ignoriert."
          checked={includeAllParticipants}
          onChange={(e) => setIncludeAllParticipants(e.currentTarget.checked)}
        />

        <MultiSelect
          label="🎭 Rollen"
          placeholder="Rolle(n) wählen…"
          data={(rolesQ.data ?? []).map((r) => ({ value: r.id, label: r.name }))}
          value={selectedRoleIds}
          onChange={setSelectedRoleIds}
          searchable
          disabled={includeAllParticipants}
        />

        <MultiSelect
          label="👤 Bestimmte Nutzer"
          placeholder="Nutzer wählen…"
          data={(allProfilesQ.data ?? []).map((p) => ({
            value: p.id,
            label: [p.first_name, p.last_name].filter(Boolean).join(' ') || p.nickname || p.id.slice(0, 8),
          }))}
          value={selectedUserIds}
          onChange={setSelectedUserIds}
          searchable
          disabled={includeAllParticipants}
        />

        <MultiSelect
          label="📋 Einzelne Einreichungen"
          placeholder="Anmeldungen wählen…"
          data={submissionOptions}
          value={selectedSubmissionIds}
          onChange={setSelectedSubmissionIds}
          searchable
          disabled={includeAllParticipants}
        />

        <Switch
          label="⚙ Regelbasiert ergänzen"
          description="Optional zusätzlich zu Rollen/Nutzern/Einreichungen."
          checked={ruleEnabled}
          onChange={(e) => setRuleEnabled(e.currentTarget.checked)}
          disabled={includeAllParticipants}
        />

        {ruleEnabled && (
          <Stack gap="xs">
            <Select
              label="Feld"
              placeholder="Formularfeld wählen"
              data={fieldOptions}
              value={ruleFieldKey || null}
              onChange={(v) => setRuleFieldKey(v ?? '')}
              searchable
              disabled={includeAllParticipants}
            />
            <Group grow>
              <Select
                label="Operator"
                value={ruleOperator}
                onChange={(v) => setRuleOperator(v ?? 'contains')}
                data={[
                  { value: 'contains',    label: 'enthält' },
                  { value: 'equals',      label: 'gleich' },
                  { value: 'starts_with', label: 'beginnt mit' },
                ]}
                disabled={includeAllParticipants}
              />
              <TextInput
                label="Wert"
                placeholder="z.B. Tänzer"
                value={ruleValue}
                onChange={(e) => setRuleValue(e.currentTarget.value)}
                disabled={includeAllParticipants}
              />
            </Group>
          </Stack>
        )}

        <Divider />

        {/* Visibility */}
        <Switch
          label="In App anzeigen"
          description="Wenn deaktiviert, ist dieser Eintrag für Teilnehmer in der App nicht sichtbar"
          checked={isAppVisible}
          onChange={(e) => setIsAppVisible(e.currentTarget.checked)}
        />
        {isAppVisible && (
          <DatePickerInput
            label="Sichtbar bis (optional)"
            description="Nach diesem Datum wird der Eintrag automatisch ausgeblendet"
            placeholder="Kein Enddatum"
            value={visibleUntilStr ? dateToLocal(visibleUntilStr) : null}
            onChange={(val: unknown) => {
              if (val instanceof Date) {
                setVisibleUntilStr(`${val.getFullYear()}-${String(val.getMonth() + 1).padStart(2, '0')}-${String(val.getDate()).padStart(2, '0')}`);
              } else {
                setVisibleUntilStr('');
              }
            }}
            clearable
            locale="de"
            minDate={dateStr ? dateToLocal(dateStr) : undefined}
          />
        )}

        <Group justify="flex-end" mt="xs">
          <Button variant="default" onClick={onClose}>Abbrechen</Button>
          <Button
            onClick={() => saveMutation.mutate()}
            loading={saveMutation.isPending}
            disabled={startTime >= endTime}
          >
            {isEdit ? 'Speichern' : 'Erstellen'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
